import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import StatusBadge from "./StatusBadge";
import { Mail, Loader2, RotateCcw, CheckCircle2, Send, Trash2, XCircle } from "lucide-react";
import type { Database } from "@/integrations/supabase/types";

type Row = Database["public"]["Tables"]["newsletter_subscribers"]["Row"];

interface Props {
  subscriber: Row | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  updating: boolean;
  onResendConfirmation: (row: Row) => void;
  onMarkConfirmed: (id: string) => void;
  onUnsubscribe: (id: string) => void;
  onResubscribe: (id: string) => void;
  onDelete: (id: string, email: string) => void;
  onSetLocale?: (id: string, locale: "en" | "sv") => void;
}

const sourceLabels: Record<string, string> = {
  footer: "Footer",
  newsletter_page: "Newsletter page",
  post_cta: "Post CTA",
};

const interestLabels: Record<string, string> = {
  ai_strategy: "AI strategy",
  governance_compliance: "Governance & compliance",
  ai_literacy_training: "AI literacy & training",
  case_studies: "Case studies",
  events_workshops: "Events & workshops",
  tools_playbooks: "Tools & playbooks",
};

const fmtDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const statusOf = (r: Row): { label: string; tone: "success" | "info" | "warn" } => {
  if (r.unsubscribed_at) return { label: "Unsubscribed", tone: "warn" };
  if (r.confirmed_at) return { label: "Active", tone: "success" };
  return { label: "Pending", tone: "info" };
};

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
    <div className="font-medium text-sm mt-0.5 break-words">{value || <span className="text-muted-foreground font-normal">—</span>}</div>
  </div>
);

const SubscriberDetailDrawer = ({
  subscriber,
  open,
  onOpenChange,
  updating,
  onResendConfirmation,
  onMarkConfirmed,
  onUnsubscribe,
  onResubscribe,
  onDelete,
  onSetLocale,
}: Props) => {
  if (!subscriber) return null;
  const st = statusOf(subscriber);
  const isUnsub = !!subscriber.unsubscribed_at;
  const isPending = !subscriber.confirmed_at && !subscriber.unsubscribed_at;
  const sizeLabel = subscriber.organization_size ? `${subscriber.organization_size} people` : "";
  const currentLocale = (subscriber as Row & { locale?: string | null }).locale === "sv" ? "sv" : "en";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <StatusBadge label={st.label} tone={st.tone} />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">
              {sourceLabels[subscriber.source] ?? subscriber.source}
            </span>
            <span className="text-xs text-muted-foreground uppercase tracking-wider">
              · {currentLocale.toUpperCase()}
            </span>
          </div>
          <SheetTitle className="text-2xl break-all">{subscriber.email}</SheetTitle>
          <SheetDescription>Subscribed {fmtDateTime(subscriber.created_at)}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <section className="grid grid-cols-2 gap-4">
            <Field label="First name" value={subscriber.first_name} />
            <Field label="Role / title" value={subscriber.role_title} />
            <Field label="Organization" value={subscriber.organization} />
            <Field label="Organization size" value={sizeLabel} />
            <Field
              label="Language"
              value={
                onSetLocale ? (
                  <div className="inline-flex rounded-md border border-border overflow-hidden">
                    <button
                      type="button"
                      onClick={() => onSetLocale(subscriber.id, "en")}
                      disabled={updating || currentLocale === "en"}
                      className={`px-2 py-0.5 text-xs ${currentLocale === "en" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                    >
                      EN
                    </button>
                    <button
                      type="button"
                      onClick={() => onSetLocale(subscriber.id, "sv")}
                      disabled={updating || currentLocale === "sv"}
                      className={`px-2 py-0.5 text-xs border-l border-border ${currentLocale === "sv" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                    >
                      SV
                    </button>
                  </div>
                ) : (
                  currentLocale.toUpperCase()
                )
              }
            />
          </section>

          <section>
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Interests</div>
            {subscriber.interests && subscriber.interests.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {subscriber.interests.map((i) => (
                  <span
                    key={i}
                    className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-xs text-foreground"
                  >
                    {interestLabels[i] ?? i.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">None selected</div>
            )}
          </section>

          <section className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <a href={`mailto:${subscriber.email}`}>
                <Mail className="w-4 h-4 mr-2" /> Email
              </a>
            </Button>
            {isPending && (
              <>
                <Button onClick={() => onResendConfirmation(subscriber)} disabled={updating}>
                  {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Resend confirmation
                </Button>
                <Button variant="outline" onClick={() => onMarkConfirmed(subscriber.id)} disabled={updating}>
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Mark confirmed
                </Button>
              </>
            )}
            {isUnsub ? (
              <Button variant="outline" onClick={() => onResubscribe(subscriber.id)} disabled={updating}>
                <RotateCcw className="w-4 h-4 mr-2" /> Re-activate
              </Button>
            ) : (
              !isPending && (
                <Button variant="outline" onClick={() => onUnsubscribe(subscriber.id)} disabled={updating}>
                  <XCircle className="w-4 h-4 mr-2" /> Mark unsubscribed
                </Button>
              )
            )}
            <Button
              variant="outline"
              onClick={() => onDelete(subscriber.id, subscriber.email)}
              disabled={updating}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          </section>

          <section className="text-xs text-muted-foreground space-y-1 pt-4 border-t border-border">
            <div>
              <span className="uppercase tracking-wider">Subscribed:</span> {fmtDateTime(subscriber.created_at)}
            </div>
            <div>
              <span className="uppercase tracking-wider">Confirmation sent:</span> {fmtDateTime(subscriber.confirmation_sent_at)}
            </div>
            <div>
              <span className="uppercase tracking-wider">Confirmed:</span> {fmtDateTime(subscriber.confirmed_at)}
            </div>
            <div>
              <span className="uppercase tracking-wider">Unsubscribed:</span> {fmtDateTime(subscriber.unsubscribed_at)}
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default SubscriberDetailDrawer;
